import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPrompts,
  updatePrompt,
  type PromptTemplate,
  type PromptType,
} from '../api/admin';
import { AppShell } from '../components/AppShell';
import { SectionCard } from '../components/SectionCard';

const LABELS: Record<PromptType, string> = {
  CLASSIFICATION: 'Klassifikation (BUG vs USER_STORY)',
  REFINEMENT_USER_STORY: 'Aufbereitung User Story (Gherkin)',
  REFINEMENT_BUG: 'Aufbereitung Bug (Repro + Kontext)',
};

export function AdminPrompts() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'prompts'],
    queryFn: listPrompts,
  });

  return (
    <AppShell crumbs={['Mindwright', 'Admin', 'Prompts']}>
      <div className="page" style={{ paddingTop: '2rem' }}>
        <SectionCard title="System-Prompts">
          <p style={{ margin: 0, fontSize: 'var(--fz-md)', color: 'var(--fg-muted)', lineHeight: 1.5 }}>
            Änderungen werden sofort wirksam. Der Worker memoisiert Prompts 60 Sekunden — neue Jobs
            sehen den neuen Text spätestens nach einer Minute.
          </p>
        </SectionCard>
        {isLoading && (
          <div className="alert-banner alert-progress">
            <span className="spinner-dot" />
            Lade …
          </div>
        )}
        {isError && (
          <div className="alert-banner alert-error">Fehler beim Laden.</div>
        )}
        {data && data.map((p) => <PromptCard key={p.type} prompt={p} />)}
      </div>
    </AppShell>
  );
}

type CardProps = {
  readonly prompt: PromptTemplate;
};

function PromptCard({ prompt }: CardProps) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState(prompt.content);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setContent(prompt.content);
    setDirty(false);
  }, [prompt.content]);

  const mutation = useMutation({
    mutationFn: (next: string) => updatePrompt(prompt.type, next),
    onSuccess: (updated) => {
      queryClient.setQueryData<readonly PromptTemplate[]>(['admin', 'prompts'], (prev) =>
        prev?.map((p) => (p.type === updated.type ? updated : p)),
      );
      setDirty(false);
    },
  });

  return (
    <SectionCard
      title={LABELS[prompt.type]}
      meta={`zuletzt ${new Date(prompt.updatedAt).toLocaleString('de-DE')}`}
    >
      <textarea
        className="textarea monospace"
        rows={10}
        value={content}
        onChange={(e) => {
          setContent(e.target.value);
          setDirty(e.target.value !== prompt.content);
        }}
      />
      {mutation.isError && (
        <div className="alert-banner alert-error" style={{ marginTop: '0.75rem' }}>
          Speichern fehlgeschlagen.
        </div>
      )}
      <div className="form-actions">
        <button
          type="button"
          className="btn-ghost-app"
          disabled={!dirty || mutation.isPending}
          onClick={() => {
            setContent(prompt.content);
            setDirty(false);
          }}
        >
          Zurücksetzen
        </button>
        <button
          type="button"
          className="btn-primary-app"
          disabled={!dirty || mutation.isPending || content.trim().length < 10}
          onClick={() => mutation.mutate(content)}
        >
          {mutation.isPending ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </SectionCard>
  );
}
